import { RECEIPT_MAX_BYTES } from '@split/shared';
import { useQueryClient, type Mutation } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { isApiError, onUnauthenticated } from '../api/http';
import { mk, qk } from '../api/keys';
import { useSession } from '../api/queries';
import {
  awaitsRetry,
  endSessionIfSignedOutElsewhere,
  isFinalRejection,
  noteSignedInUser,
  watchOtherTabsSignIn,
  watchOtherTabsSignOut,
  type DeleteExpenseVars,
  type UpdateExpenseVars,
  type UploadReceiptVars,
} from '../api/queryClient';
import { leftMemberNames, retryMutation, titleValues } from '../components/expenses/expenseActions';
import { useToast } from '../components/ui/toast-context';
import { useT } from '../lib/i18n';
import { commonMessages } from '../lib/i18n/messages/common';
import { expenseMessages } from '../lib/i18n/messages/expenses';
import { addNotice, takeNotices, type Notice } from '../lib/notices';

const MAX_MB = RECEIPT_MAX_BYTES / (1024 * 1024);

/** What to tell the user about a receipt upload the API rejected for good. */
function receiptRejection(error: unknown, vars: UploadReceiptVars | undefined): Notice {
  const values = { ...titleValues(vars?.title), max: MAX_MB };
  if (isApiError(error, 'PAYLOAD_TOO_LARGE')) return [{ key: 'receiptRejectedTooLarge', values }];
  if (isApiError(error, 'UNSUPPORTED_MEDIA_TYPE')) return [{ key: 'receiptRejectedType', values }];
  return [{ key: 'receiptRejected', values }];
}

function hidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * App-wide listeners: 401 → signed out; signed out in another tab → this one too; a different
 * user signed in (here or in another tab) → previous user's state reset; queued edit, delete or
 * receipt upload failed → toast (or a notice kept for later if the app is in the background);
 * service worker deep links → navigate.
 */
export function SessionEffects() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT(expenseMessages);
  const tc = useT(commonMessages);
  const user = useSession().data;

  // Sign-in screens call `setSessionUser`; this also covers a session that changed elsewhere.
  useEffect(() => {
    if (user && noteSignedInUser(client, user.id)) client.setQueryData(qk.session, user);
  }, [client, user]);

  useEffect(() => watchOtherTabsSignIn(client), [client]);

  const showSignedOut = useCallback(() => void navigate('/signin', { replace: true }), [navigate]);
  useEffect(() => watchOtherTabsSignOut(client, showSignedOut), [client, showSignedOut]);

  const render = useCallback(
    (notice: Notice) => notice.map((p) => t(p.key, p.values)).join(' '),
    [t],
  );

  // Notices kept while the app was in the background (or closed): shown once the user is back.
  useEffect(() => {
    const show = () => {
      if (hidden()) return;
      for (const n of takeNotices()) toast({ message: render(n), tone: 'error' });
    };
    show();
    document.addEventListener('visibilitychange', show);
    return () => document.removeEventListener('visibilitychange', show);
  }, [toast, render]);

  // These run in the background (queued offline, maybe replayed after a reload), so nobody
  // else is left to tell the user they failed.
  useEffect(() => {
    /** Toast now, or keep it for when the user is back if nobody would see it. */
    const report = (notice: Notice, retry?: Mutation) => {
      if (hidden()) {
        addNotice(notice);
        return;
      }
      toast({
        message: render(notice),
        tone: 'error',
        action: retry
          ? { label: tc('retry'), onClick: () => retryMutation(client, retry) }
          : undefined,
      });
    };
    return client.getMutationCache().subscribe((e) => {
      const m = e.mutation as Mutation | undefined;
      if (e.type !== 'updated' || e.action.type !== 'error' || !m) return;
      const key = m.options.mutationKey?.[0];
      const error: unknown = m.state.error;
      if (key === mk.deleteExpense[0]) {
        const vars = m.state.variables as DeleteExpenseVars | undefined;
        const names = vars ? leftMemberNames(client, vars.groupId, error, t('someone')) : null;
        report(
          names
            ? [{ key: 'leftMembersDelete', values: { names, ...titleValues(vars?.title) } }]
            : [{ key: 'queuedDeleteFailed' }],
        );
        return;
      }
      if (key === mk.uploadReceipt[0]) {
        const vars = m.state.variables as UploadReceiptVars | undefined;
        report(
          isFinalRejection(error)
            ? receiptRejection(error, vars)
            : [{ key: 'receiptUploadFailed' }],
        );
        return;
      }
      if (m.meta?.reportConflict !== true) return;
      const vars = m.state.variables as UpdateExpenseVars | undefined;
      const title = vars?.body.title ?? '';
      const left = vars ? leftMemberNames(client, vars.groupId, error, t('someone')) : null;
      const photo = vars?.hasReceipt === true && isFinalRejection(error);
      const notice: Notice = isApiError(error, 'VERSION_CONFLICT')
        ? [{ key: 'versionConflictQueued', values: { title } }]
        : left
          ? [
              { key: 'queuedEditFailed', values: { title } },
              { key: 'leftMembersChanged', values: { names: left } },
            ]
          : awaitsRetry(m)
            ? [{ key: 'queuedEditRetryable', values: { title } }]
            : [{ key: 'queuedEditFailed', values: { title } }];
      report(
        photo ? [...notice, { key: 'photoDiscarded' }] : notice,
        awaitsRetry(m) ? m : undefined,
      );
      if (vars) void client.invalidateQueries({ queryKey: qk.expense(vars.expenseId) });
      void client.invalidateQueries({ queryKey: ['groupFeed'] });
    });
  }, [client, toast, t, tc, render]);

  useEffect(
    () =>
      onUnauthenticated(() => {
        // Signed out (or someone else signed in) in a tab this one did not hear from.
        if (endSessionIfSignedOutElsewhere(client)) showSignedOut();
        else if (client.getQueryData(qk.session)) client.setQueryData(qk.session, null);
      }),
    [client, showSignedOut],
  );

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; url?: string } | null;
      if (data?.type === 'navigate' && typeof data.url === 'string' && data.url.startsWith('/')) {
        void navigate(data.url);
      }
      if (data?.type === 'push-received' || data?.type === 'settlement-updated') {
        void client.invalidateQueries({ queryKey: qk.notifications });
        void client.invalidateQueries({ queryKey: qk.unreadCount });
        void client.invalidateQueries({ queryKey: qk.attention });
      }
      // Confirmed / rejected from a notification action: the worker doesn't know the group.
      if (data?.type === 'settlement-updated') {
        const keys = ['overview', 'groups', 'people', 'person', 'activity', 'groupFeed'];
        for (const key of [...keys, 'groupBalances', 'settlement']) {
          void client.invalidateQueries({ queryKey: [key] });
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate, client]);

  return null;
}
